import {
  IWorkOrder,
  IProductionWorkTime,
  IWorkOrderMaterialLine,
  IWorkOrderStep,
  IProductionOrderMaterialRegistration,
  IWorkOrderPlan,
  ICodeVisualCode,
  IProductionResource,
  IWorkOrderResource,
  IIProductionDao$IProductionOrderPlanningUpdate,
  IProductionOrderConfirmation,
  IWorkOrderStepTestPlan,
  IIProductionDao$ITestResult,
  IWorkOrderLicensePlateProgress,
  IIProductionDao$ISaveTimeWorkedFlushingResult,
  IHint,
  IHintRequest,
  IBinLocation,
  IBatch,
  ISerialNumber,
  IItem,
} from 'interfaces';
import { Registry } from '.';
import { saveBase64UrlEncode } from 'component_utils/utils';

export type IWorkOrderStepMap = { [k: string]: IWorkOrderStep[] };
export type IWorkOrderResourceMap = { [k: string]: IWorkOrderResource[] };

export default ({ get, post, destroy }: Registry) => {
  return {
    production: {
      createOrder: {
        getWorkOrderPlans: (itemCode: string) => get<IWorkOrderPlan[]>(`/api/v1/production/${saveBase64UrlEncode(itemCode)}/workOrderPlans`),
        createOrder: (
          whsCode: string,
          itemCode: string,
          workOrderPlanCode: string,
          targetQuantity: string,
          linkedSalesOrderCode: string,
          releaseAfterCreate: boolean,
        ) => {
          const params: any = {
            whsCode,
            itemCode,
            workOrderPlanCode,
            targetQuantity,
            releaseAfterCreate,
          }
          if (linkedSalesOrderCode) {
            params.linkedSalesOrderCode = linkedSalesOrderCode
          }
          return post<ICodeVisualCode>(
            '/api/v1/production/createProductionOrder',
            {},
            params
          )
        }
      },

      updatePlanning: (changes: IIProductionDao$IProductionOrderPlanningUpdate[]) => post('/api/v1/production/updatePlanning', changes),

      getWorkOrders: (codes: string[]) => post<IWorkOrder[]>(`/api/v1/production/get_multiple_work_orders`, codes),
      findWorkOrders: (codes: string[]) => post<IWorkOrder[]>(`/api/v1/production/find_multiple_work_orders`, codes),
      getOpenWorkOrders: (source: string) => get<IWorkOrder[]>(`/api/v1/production/work_orders`, { source }),
      getLicensePlatesUsedInWorkOrder: (code: string): Promise<string[]> =>
        get(`/api/v1/production/work_orders/${code}/licensePlates`),
      getLicensePlateProgress: (code: string, licensePlate: string): Promise<IWorkOrderLicensePlateProgress> =>
        get(`/api/v1/production/work_orders/${code}/licensePlateProgress`, { licensePlate }),
      getWorkOrderLines: (code: string): Promise<IWorkOrderMaterialLine[]> =>
        get(`/api/v1/production/work_orders/${code}/lines`),
      getWorkOrderLinesForMultiple: (codes: string[]): Promise<IWorkOrderMaterialLine[]> =>
        post(`/api/v1/production/work_orders/lines_for_multiple`, codes),
      getWorkOrderSteps: (code: string): Promise<IWorkOrderStep[]> =>
        get(`/api/v1/production/work_orders/${code}/steps`),
      getWorkOrderStepTestPlan: (code: string, stepCode: string): Promise<IWorkOrderStepTestPlan> =>
        get(`/api/v1/production/work_orders/${code}/steps/${stepCode}/test_plan`),
      getWorkOrderStepsForMultipleAsMap: (codes: string[]): Promise<IWorkOrderStepMap> =>
        post(`/api/v1/production/work_orders/steps`, codes),
      getWorkOrderResourcesForMultipleAsMap: (codes: string[]): Promise<IWorkOrderResourceMap> =>
        post(`/api/v1/production/work_orders/resources`, codes),
      getResources: (): Promise<IProductionResource[]> => get(`/api/v1/production/resources`),
      getMaterialHints: (
        requests: IHintRequest[],
        forceLocation: IBinLocation,
      ) => post<IHint[]>('/api/v1/production/material_configuration/hints', { requests, forceLocation }),

      printWorkOrderDocument: (code: string, printerId: string) => post(`/api/v1/production/order/${code}/print/${printerId}`, {}),

      getWorkTimes: (): Promise<IProductionWorkTime[]> => get('/api/v1/production/work_confirmation'),
      getAllWorkTimes: (): Promise<IProductionWorkTime[]> => get('/api/v1/production/work_confirmation/all'),
      saveWorkTimes: (entry: IProductionWorkTime) => post('/api/v1/production/work_confirmation', entry),
      deleteWorkTimes: (id: string) => destroy(`/api/v1/production/work_confirmation/${id}`),

      markOrderPushedToKardex: (code: string) => post(`/api/v1/production/work_orders/${code}/markPushedToKardex`, {}),

      markStepCompleted: (code: string, stepCode: string, licensePlate: string) => post(
        `/api/v1/production/work_orders/${code}/steps/${stepCode}/markCompleteForLicensePlate`, 
        {},
        { licensePlate }
      ),
      materialRegistration: (code: string, registration: IProductionOrderMaterialRegistration): Promise<void> =>
        post(`/api/v1/production/work_orders/${code}/materials`, registration),
      flushWorkTimes: (ids: string[]): Promise<IIProductionDao$ISaveTimeWorkedFlushingResult[]> =>
        post('/api/v1/production/work_confirmation/flush', ids),
      confirmWorkOrder: (
        code: string,
        payload: IProductionOrderConfirmation
      ): Promise<{ location: IBinLocation, item: IItem, batch: IBatch, serial: ISerialNumber, quantity: number }[]> =>
        post(`/api/v1/production/work_orders/${code}/confirm`, payload),
      confirmTest: (
        code: string,
        stepCode: string,
        payload: IIProductionDao$ITestResult
      ) => post(`/api/v1/production/work_orders/${code}/steps/${stepCode}/test_registration`, payload),

      getSettingsForMaterialConfirmation: (order: IWorkOrder, step: IWorkOrderStep, licensePlate: string): Promise<any> =>
        post(`/api/v1/production/settings/materialConfirmationConfiguration`, { order, step, licensePlate }),  

      getSettingsForProductConfirmation: (order: IWorkOrder, licensePlate: string): Promise<any> =>
        post(`/api/v1/production/settings/productionConfirmationConfiguration`, order, { licensePlate }),  

      admin: {
        getWorkTimes: (): Promise<IProductionWorkTime[]> => get('/api/v1/production/work_confirmation/all'),
      },
    },
  };
};
